<template>
  <div class="change col-xl-5 mx-auto">
    <b-form-group label="Type of Change" :state="IsFieldValid($v.change.changeTypeID)" :invalid-feedback="RequiredMessage('Change Type')">
      <multiselect 
        v-model="$v.change.changeTypeID.$model" 
        :options="options.map(o => o.changeTypeID)" 
        placeholder="Select a change type"
        :show-labels="false"
        :searchable="false"
        :allow-empty="false"
        :option-height="80"
        :max-height="600"
        open-direction="bottom"
        :disabled="isViewOnly">
        <template slot="singleLabel" slot-scope="props">
          <span>{{ options.find(o => o.changeTypeID == props.option).shortDescription }}</span>
        </template>
        <template slot="option" slot-scope="props">
          <div class="short-description">{{ options.find(o => o.changeTypeID == props.option).shortDescription }}</div>
          <div class="long-description">{{ options.find(o => o.changeTypeID == props.option).description }}</div>
        </template>
      </multiselect>
    </b-form-group>

    <b-form-group>
      <b-form-row>
        <b-form-group label="Effective Date" class="col-6" :state="IsFieldValid($v.change.effectiveDate)" :invalid-feedback="RequiredMessage('Effective Date')">
          <datepicker 
          :value="change.effectiveDate ? moment(change.effectiveDate).toDate() : ''" 
          @selected="$v.change.effectiveDate.$model = $event ? moment($event).format('YYYY-MM-DD') : ''"
          input-class="form-control" 
          format="MM/dd/yyyy" 
          typeable
          :disabled="isViewOnly"></datepicker>
        </b-form-group>
        <b-form-group label="Reported Date" class="col-6" :state="IsFieldValid($v.change.reportedDate)" :invalid-feedback="reportedDateInvalidMessage($v.change.reportedDate, 'Reported Date')">
          <datepicker :value="change.reportedDate ? moment(change.reportedDate).toDate() : ''" 
          @selected="$v.change.reportedDate.$model = $event ? moment($event).format('YYYY-MM-DD') : ''" 
          :disabledDates="{ from: moment().toDate() }"
          input-class="form-control" 
          format="MM/dd/yyyy" 
          typeable
          :disabled="isViewOnly"></datepicker>
        </b-form-group>
      </b-form-row>
    </b-form-group>

    <b-form-group label="Please provide a brief description of the change(s)." :state="IsFieldValid($v.change.narrative)" :invalid-feedback="narrativeInvalidMessage($v.change.narrative, 'Description')">
      <b-form-textarea 
        v-model="$v.change.narrative.$model" 
        :state="IsFieldValid($v.change.narrative)"
        :disabled="isViewOnly" 
        rows="5"></b-form-textarea>
    </b-form-group>
    <b-form-group label="Is there a spousal relationship between the new and prior owners?" :state="IsFieldValid($v.change.isSpousalRelationship)" :invalid-feedback="RequiredMessage('Spousal Relationship')">
      <b-form-radio-group 
        :options="[{text: 'Yes', value: true}, {text: 'No', value: false}]" 
        v-model="$v.change.isSpousalRelationship.$model" 
        :state="IsFieldValid($v.change.isSpousalRelationship)"
        :disabled="isViewOnly"></b-form-radio-group>
    </b-form-group>
    <b-form-group
      label="Spousal: Please identify the individuals and their relationship."
      v-if="change.isSpousalRelationship"
      :state="IsFieldValid($v.change.spousalRelationshipReason)"
      :invalid-feedback="narrativeInvalidMessage($v.change.spousalRelationshipReason, 'Spousal relationship')"
      :disabled="isViewOnly"
    >
      <b-form-textarea 
        v-model="$v.change.spousalRelationshipReason.$model" 
        :state="IsFieldValid($v.change.spousalRelationshipReason)" 
        rows="5"
        :disabled="isViewOnly"></b-form-textarea>
    </b-form-group>
  </div>
</template>

<script>
import { required, requiredIf, maxLength, minValue } from 'vuelidate/lib/validators'
import Multiselect from 'vue-multiselect'
const cantBeFuture = function(date) {
  return this.moment(date).isSameOrBefore(new Date(), 'day')
}

export default {
  name: 'Change',
  props: {
    changeProp: {
      type: Object,
      required: true
    }
  },
  components: { 
    Multiselect 
  },
  data() {
    return {
      change: this.changeProp,
      dirty: false
    }
  },
  validations: {
    change: {
      changeTypeID: {
        minValue: minValue(1)
      },
      effectiveDate: {
        required
      },
      reportedDate: {
        required,
        cantBeFuture
      },
      narrative: {
        required,
        maxLength: maxLength(1000)
      },
      isSpousalRelationship: {
        required
      },
      spousalRelationshipReason: {
        required: requiredIf(function(model) {
          return model.isSpousalRelationship
        }),
        maxLength: maxLength(1000)
      }
    }
  },
  methods: {
    reportedDateInvalidMessage(date, name) {
      return !date.required ? this.RequiredMessage(name) : `${name} must be on or before today's date.`
    },
    narrativeInvalidMessage(field, name) {
      return !field.required ? this.RequiredMessage(name) : this.MaxLengthMessage(name, field.$params.maxLength.max)
    },
    backClicked() {
      this.$router.push({
        name: 'change/list',
        params: {
          submissionId: this.change.submissionID
        }
      })
    },
    nextClicked() {
      if (this.isViewOnly) {
        this.$router.push({
          name: 'entity/list',
          params: {
            submissionId: this.change.submissionID,
            changeId: this.change.changeID
          }
        })
      } else {
        this.$v.$touch()
        if (this.$v.$invalid) return

        this.$store.dispatch('change/save', this.change).then(() => {
          this.dirty = false
          const c = this.$store.state.change.change
          this.$router.push({
            name: 'entity/list',
            params: {
              submissionId: c.submissionID,
              changeId: c.changeID
            }
          })
        })
      }
    }
  },
  computed: {
    isViewOnly() {
      return this.$store.getters['application/isChangeViewOnly']
    },
    changes() {
      return this.$store.state.change.changes
    },
    options() {
      //filter out the change types that don't allow multiple and already have another change on this submission with that change type
      return this.$store.state.application.changeTypes.filter(ct => ct.areMultipleAllowed || this.change.changeTypeID == ct.changeTypeID || !this.changes.filter(c => c.changeTypeID == ct.changeTypeID).length)
    }
  },
  watch: {
    change: {
      handler() {
        this.dirty = true
      },
      deep: true
    }
  },
  created() {
    this.$EventBus.$on('back-clicked', this.backClicked)
    this.$EventBus.$on('next-clicked', this.nextClicked)
  },
  beforeDestroy() {
    this.$EventBus.$off('back-clicked', this.backClicked)
    this.$EventBus.$off('next-clicked', this.nextClicked)
  },
  beforeRouteLeave(to, from, next) {
    if (!this.dirty) return next()
    
    this.ConfirmLeave().then(answer => {
      if (answer == this.ConfirmLeaveEventTypes.Discard) return next()
      else if (answer == this.ConfirmLeaveEventTypes.Save) {
        this.$v.$touch()
        if (this.$v.$invalid) return next(false)
        else return this.$store.dispatch('change/save', this.change).then(() => next())
      }
      else return next(false)
    })
  }
}
</script>

<style scoped>
.short-description {
  font-weight: bold !important;
}

.long-description {
  font-style: italic !important;
  font-size: 14px !important;
  color: #545454 !important;
  display: table !important;
  white-space: normal !important;
}

.change {
  min-height: 80vh !important;
}
</style>